<template>
  <NuxtLink :to="linkTo" class="block">
    <div class="rounded-lg border bg-card text-card-foreground shadow-sm hover:shadow-lg transition-shadow cursor-pointer h-full">
      <div class="flex flex-col space-y-1.5 p-6 pb-3">
        <div class="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div class="flex items-center gap-2 flex-wrap">
            <UiBadge variant="secondary" class="text-xs capitalize">
              {{ category }}
            </UiBadge>
            <UiBadge
              v-if="trending"
              variant="default"
              class="bg-foreground hover:bg-foreground/90 text-background text-xs"
            >
              <Icon
                name="tabler:trending-up"
                size="12"
                class="mr-1"
              />
              Trending
            </UiBadge>
          </div>
          <span class="text-xs text-gray-500 whitespace-nowrap">
            {{ formatDate(publishedDate) }}
          </span>
        </div>
        <h3 class="text-lg sm:text-xl font-semibold leading-none tracking-tight mb-2 line-clamp-2">
          {{ title }}
        </h3>
        <p class="text-sm text-muted-foreground line-clamp-2 sm:line-clamp-3">
          {{ description }}
        </p>
      </div>
      <div class="p-6 pt-0">
        <!-- Tags for blog posts -->
        <div
          v-if="tags && tags.length > 0"
          class="flex flex-wrap gap-1 mb-3 max-h-16 overflow-hidden"
        >
          <UiBadge
            v-for="tag in tags.slice(0, 3)"
            :key="tag"
            variant="outline"
            class="text-xs"
          >
            {{ tag }}
          </UiBadge>
          <UiBadge
            v-if="tags.length > 3"
            variant="outline"
            class="text-xs text-gray-400"
          >
            +{{ tags.length - 3 }}
          </UiBadge>
        </div>

        <div class="flex items-center justify-between text-xs text-gray-500">
          <div class="flex items-center gap-3 flex-wrap">
            <div v-if="readingTime" class="flex items-center whitespace-nowrap">
              <Icon
                name="tabler:clock"
                size="12"
                class="mr-1"
              />
              {{ readingTime }}
              {{ readingTimeLabel }}
            </div>
            <div v-if="contentType" class="flex items-center whitespace-nowrap">
              <Icon
                :name="contentTypeIcon"
                size="12"
                class="mr-1"
              />
              {{ contentType }}
            </div>
            <div v-if="difficulty" class="flex items-center whitespace-nowrap">
              <Icon
                name="tabler:chart-bar"
                size="12"
                class="mr-1"
              />
              {{ difficulty }}
            </div>
          </div>
          <div class="flex items-center gap-2 sm:gap-3">
            <div v-if="viewCount" class="flex items-center whitespace-nowrap">
              <Icon name="tabler:eye" size="12" class="mr-1" />
              {{ formatCount(viewCount) }}
            </div>
            <div
              v-if="shareCount"
              class="hidden sm:flex items-center whitespace-nowrap"
            >
              <Icon
                name="tabler:share-2"
                size="12"
                class="mr-1"
              />
              {{ formatCount(shareCount) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import { useI18n } from '#imports';
import type { CardProps } from '#types/search';

const { $getLocale } = useI18n();

type Props = Omit<CardProps, 'contentType' | 'contentTypeIcon' | 'readingTimeLabel'> & {
  contentType?: string;
  contentTypeIcon?: string;
  readingTimeLabel?: string;
};

withDefaults(defineProps<Props>(), {
  trending: false,
  readingTimeLabel: 'min read',
  contentTypeIcon: 'tabler:file-text',
});

// Utility functions for formatting
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString($getLocale(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatCount = (count: number) => {
  if (count >= 1000000) {
    return Math.floor(count / 1000000) + 'M';
  }
  if (count >= 1000) {
    return Math.floor(count / 1000) + 'k';
  }
  return count.toString();
};
</script>
